import React, {useCallback, useEffect} from "react";
import {Box} from "@mui/material";
import DOMPurify from "dompurify";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {NODE_TYPE_DATAPORTAL} from "../../constants/nodes";
import Call from "../../hocs/call";
import CustomEmpty from "../custom-empty";
import MetadataJsonViewer from "./MetadataJsonViewer";
import {
  clearPackageMetadata,
  fetchDataportalPackageMetadata,
  fetchDataportalPackageMetadataSchema,
  fetchPackageMetadata
} from "../../state/miscellaneous/miscellaneousActions";
import {
  metadataFetchedSelector,
  metadataHtmlSelector,
  metadataJsonSelector,
  metadataSchemaSelector,
  metadataUrlSelector
} from "../../state/miscellaneous/miscellaneousSelectors";

export const PACKAGE_METADATA_TYPE_JSON = "json";
export const PACKAGE_METADATA_TYPE_HTML = "html";

const iframeCommonProps = {
  title: "title",
  style: {
    border: 0,
    width: "100%",
    height: "100%"
  }
};

const PackageMetadataViewer = ({
  nodeId,
  nodeType,
  catalog: externalcatalog,
  packageId,
  metadataUrl,
  metadataType = PACKAGE_METADATA_TYPE_JSON
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const catalog = useSelector(state => (externalcatalog ? externalcatalog : state.catalog));
  const packageMap = catalog.packageMap;

  const packageMetadataSchema = useSelector(metadataSchemaSelector);

  const packageMetadataFetched = useSelector(metadataFetchedSelector);

  const packageMetadataJson = useSelector(metadataJsonSelector);
  const packageMetadataHtml = useSelector(metadataHtmlSelector);
  const packageMetadataUrl = useSelector(metadataUrlSelector);

  const fetchDPMetadataSchema = useCallback(
    ({nodeId}) => {
      dispatch(fetchDataportalPackageMetadataSchema(nodeId));
    },
    [dispatch]
  );

  const fetchDPMetadata = useCallback(
    ({nodeId, nodeType, packageId, packageMap}) => {
      dispatch(fetchDataportalPackageMetadata(nodeId, nodeType, packageId, packageMap, metadataType));
    },
    [metadataType, dispatch]
  );

  const fetchMetadata = useCallback(
    ({nodeId, packageId, packageMap}) => {
      dispatch(fetchPackageMetadata(nodeId, packageId, packageMap, metadataType));
    },
    [metadataType, dispatch]
  );

  useEffect(() => {
    return () => {
      dispatch(clearPackageMetadata());
    };
  }, [dispatch]);

  return nodeType === NODE_TYPE_DATAPORTAL ? (
    <Call cb={fetchDPMetadataSchema} cbParam={{nodeId: nodeId}} disabled={Boolean(packageMetadataSchema)}>
      <Call
        cb={fetchDPMetadata}
        cbParam={{nodeId: nodeId, nodeType: nodeType, packageId: packageId, packageMap: packageMap}}
        disabled={!packageMetadataSchema || packageMetadataFetched}
      >
        {packageMetadataJson ? (
          <MetadataJsonViewer
            metadata={packageMetadataJson}
            notes={packageMap?.[packageId]?.note}
            packageId={packageId}
          />
        ) : packageMetadataFetched ? (
          <CustomEmpty>{t("components.packageMetadataViewer.noMetadataAvailable")}</CustomEmpty>
        ) : null}
      </Call>
    </Call>
  ) : (
    <Call
      cb={fetchMetadata}
      cbParam={{nodeId: nodeId, packageId: packageId, packageMap: packageMap}}
      disabled={packageMetadataFetched}
    >
      <Box
        className="package-metadata-viewer__container"
        sx={
          packageMetadataJson
            ? null
            : {
                width: "100%",
                height: "100%"
              }
        }
      >
        {packageMetadataJson ? (
          <MetadataJsonViewer
            metadata={packageMetadataJson}
            notes={packageMap?.[packageId].note}
            packageId={packageId}
          />
        ) : packageMetadataHtml ? (
          <iframe srcDoc={DOMPurify.sanitize(packageMetadataHtml, {FORCE_BODY: true})} {...iframeCommonProps} />
        ) : packageMetadataUrl && metadataUrl ? (
          <iframe src={metadataUrl} {...iframeCommonProps} />
        ) : packageMetadataFetched ? (
          <CustomEmpty>{t("components.packageMetadataViewer.noMetadataAvailable")}</CustomEmpty>
        ) : null}
      </Box>
    </Call>
  );
};

export default PackageMetadataViewer;
